import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, Crown, MessageCircle } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const WHATSAPP_NUMBER = "37060044499";
const WHATSAPP_MESSAGE = "Hello, I'm interested in learning about luxury property investment opportunities with Agota Estate.";

interface FloatingActionHubProps {
  onOpenChat: () => void;
}

const FloatingActionHub = ({ onOpenChat }: FloatingActionHubProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 400);
      if (window.scrollY <= 400) setIsExpanded(false);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleAssess = () => {
    setIsExpanded(false);
    if (location.pathname === '/') {
      document.getElementById('investment-assessment')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/#investment-assessment');
    }
  };

  const openWhatsApp = () => {
    setIsExpanded(false);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`, '_blank');
  };

  const handleChat = () => {
    setIsExpanded(false);
    onOpenChat();
  };

  const actions = [
    { icon: <Crown className="w-4 h-4" />, label: 'Assess', onClick: handleAssess, className: 'bg-gold text-charcoal' },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      ),
      label: 'WhatsApp',
      onClick: openWhatsApp,
      className: 'bg-[#25D366] text-white',
    },
    { icon: <MessageCircle className="w-4 h-4" />, label: 'AI Advisor', onClick: handleChat, className: 'bg-charcoal text-cream' },
  ];

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          className="fixed bottom-6 right-4 md:right-6 z-40 flex flex-col items-end gap-2"
        >
          {/* Expanded actions */}
          <AnimatePresence>
            {isExpanded && actions.map((action, i) => (
              <motion.button
                key={action.label}
                initial={{ opacity: 0, y: 10, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.8 }}
                transition={{ delay: i * 0.05 }}
                onClick={action.onClick}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full shadow-lg text-sm font-medium tracking-wide ${action.className} hover:opacity-90 transition-opacity`}
              >
                {action.icon}
                <span className="font-body">{action.label}</span>
              </motion.button>
            ))}
          </AnimatePresence>

          {/* Main FAB */}
          <motion.button
            onClick={() => setIsExpanded(!isExpanded)}
            whileTap={{ scale: 0.95 }}
            className="w-12 h-12 rounded-full bg-gold text-charcoal shadow-lg shadow-gold/30 flex items-center justify-center hover:opacity-90 transition-opacity"
          >
            <motion.div animate={{ rotate: isExpanded ? 45 : 0 }} transition={{ duration: 0.2 }}>
              {isExpanded ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
            </motion.div>
          </motion.button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FloatingActionHub;
